/**
 * Round number
 * @param     {number}  [value=0]
 * @param     {number}  [precision=2]
 *
 * @returns   {number}
 */
export function round(value = 0, precision = 2) {
  if (Number.isNaN(Number(value))) return 0;
  const multiplier = 10 ** precision || 0;
  return Math.round(value * multiplier) / multiplier;
}

/**
 * Calculate average value
 * @param     {array}  [value=[2, 3, 4]]
 *
 * @returns   {number} 3
 */
export function average(arr) {
  if (arr.length === 0) return 0;
  const total = arr.reduce((prev, next) => parseFloat(prev || 0) + parseFloat(next || 0), 0);
  return round(total / arr.length);
}

/**
 * Format numbers to acceptable format
 * acceptable range number is 0 - to max (default is 100)
 * @param     {Object}  [e=events]
 *
 * @returns   {string}
 */
export const formatScore = (value = 0, max = 100) => {
  let maxValue = max;
  if (maxValue.target) maxValue = 100; // to handle second parameter was an event
  let transformedValue = value
    .toString()
    .replace(/[^\d.]/g, '') // numbers and decimals only
    .replace(/(^[\d]{3})[\d]/g, '$1') // not more than 3 digits at the beginning
    .replace(/(\..*)\./g, '$1') // decimal can't exist more than once
    .replace(/(\.[\d]{2})./g, '$1'); // not more than 2 digits after decimal

  if (maxValue !== 'unlimited' && !Number.isNaN(transformedValue) && parseFloat(transformedValue) > maxValue) transformedValue = maxValue.toString();

  return transformedValue;
};

/**
 * Format numbers to currency format
 * @param     {Number}
 *
 * @returns   {Number}
 */
export const formatCurrency = (value, isMinus) => {
  const val = parseFloat(value);
  if (!Number.isNaN(val)) return val.toLocaleString('id-ID', { maximumFractionDigits: 2 });
  return isMinus ? '-' : '';
};

/**
 * Format Input type text to accept number only
 * acceptable range number is 0 - to max (default is 100)
 * @param     {Object}  [e=events]
 *
 * @returns   {string}
 */
export const formatInputScoreFloat = (e, isNumber = false) => {
  const { max, value } = e.target;
  const val = formatScore(value, max || 100) || 0;
  return isNumber ? parseFloat(val) : val;
};

/**
 * Format Input type text to accept number only
 * acceptable range number is 0 - to max (default is 100)
 * @param     {Object}  [e=events]
 *
 * @returns   {string}
 */
export const formatInputScore = e => {
  const { max, value } = e.target;
  return formatScore(value, max || 100);
};

/**
 * Format to thousand separator
 * @param {number} [value=0]
 *
 * @returns {string}
 */
export const parseNumberSeperator = (val = 0) => {
  const value = val ? val.toString().replace(/[^0-9]/g, '') : val;
  return Number(value).toLocaleString('id-ID');
};

/**
 * Format thousand separator to number
 * @param {string} [value]
 *
 * @returns {number}
 */
export const numberSeparatorToPlain = val => {
  const value = parseFloat(val.replace(/\./g, ''), 10);
  return value;
};

/**
 * Format bytes
 * @param {number} [value=0]
 *
 * @returns {string} 5 MB
 */
export const numberToBytes = bytes => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
};

/**
 * Format decimal max 2 digits
 * @param {number} [value=0]
 *
 * @returns {string}
 */
export const formatDecimalMax = (val = 0, decimal = 2) => {
  return new Intl.NumberFormat('id-ID', { maximumFractionDigits: decimal }).format(val);
};

/**
 * Format integer number to string (ordinals in words)
 * @param {number} [value=0] Number as integer like 1, 2, 3, etc.
 *
 * @returns {string} String like first, second, third etc
 */
export const formatNumberToOrdinals = number => {
  if (number >= 11 && number <= 13) return `${number}th`;

  // Get the last digit of the number
  const lastDigit = number % 10;

  // Determine the ordinal suffix based on the last digit
  switch (lastDigit) {
    case 1:
      return `${number}st`;
    case 2:
      return `${number}nd`;
    case 3:
      return `${number}rd`;
    default:
      return `${number}th`;
  }
};

export default {
  round,
  formatScore,
  formatInputScore,
  parseNumberSeperator,
  numberSeparatorToPlain,
  numberToBytes,
  formatDecimalMax,
};
